import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { HttpStatusCode } from '../../shared/constants/http-status-code.enum';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../../shared/instrumentation/instrumented-http.service';
import { FileItem, FileType } from '../../shared/models/file-item.model';
import { HttpRequestOptions } from '../../shared/models/http-request-options.model';
import { OperationStream } from '../../shared/models/operation-stream.model';
import { MruConfigService } from '../../shared/mru/mru-config.service';
import { AccessTokenConsumer, AccessTokenService } from '../../shared/services/access-token.service';
import { OfficeHomeApiConfigService } from '../../shared/services/officehome-api-config.service';
import { SchedulerService } from '../../shared/services/scheduler.service';
import { XhrService, XhrServiceError } from '../../shared/services/xhr.service';
import { ContextService } from '../../shared/shared-context/context.service';
import { ObservableHelper } from '../../shared/utilities/observable-helper.utility';
import { TrendingDocData, TrendingDocument, TrendingErrorDataWrapper, TrendingResponse } from '../models/insights.model';

/**
 * Service that makes requests to Substrate APIs.
 */
@Injectable({
    providedIn: 'root'
})
export class SubstrateInsightsService extends XhrService {
    private readonly trendingDataStreamMap: Map<string, OperationStream<TrendingDocData>>;
    private readonly requestConfig: HttpRequestOptions;
    private static readonly trendingPath = '/api/beta/me/officegraphinsights/trending';

    constructor(
        accessTokenService: AccessTokenService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        private readonly mruConfigService: MruConfigService,
        platformLocation: PlatformLocation,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        scheduler: SchedulerService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.Substrate, contextService, officeHomeApiConfigService, platformLocation, scheduler);
        this.requestConfig = {
            headers: new Map([
                [ 'Accept', 'application/json' ],
                [ 'Prefer', 'outlook.data-source="Substrate", exchange.behavior="OfficeGraphInsights"' ],
                [ 'X-Application', 'OfficeComHome'],
                [ 'X-ApplicationScenario', 'OfficeComHome']
            ])
        };
        this.trendingDataStreamMap = new Map<string, OperationStream<TrendingDocData>>();
    }

    public static fileType(trendingDoc: TrendingDocument): FileType {
        if (!trendingDoc || !trendingDoc.ResourceVisualization || !trendingDoc.ResourceVisualization.Type) {
            return FileType.Unknown;
        }

        return FileItem.fileTypeNameToFileType(trendingDoc.ResourceVisualization.Type);
    }

    public getTrendingDocuments(count: number, types?: Set<FileType>): OperationStream<TrendingDocData> {
        const supportedTypes = this.mruConfigService.config.supportedFileTypes;
        const fileTypes = (types && types.size) ? supportedTypes.filter(type => types.has(FileItem.fileTypeNameToFileType(type))) : supportedTypes;
        const filterParams = fileTypes.reduce((acc, type, index) =>
            `${acc}${index > 0 ? ' or ' : ''}resourceVisualization/Type eq '${type}'`, '');
        const queryParams = `$top=${count}&$filter=${encodeURIComponent(filterParams)}`;

        const path = SubstrateInsightsService.trendingPath;
        const allQueryParams = `?${queryParams}`;
        const trendingUrl = this.inMockMode ? `${this.contextService.sharedContext.officeHomeApiRoot}${path}${allQueryParams}` : `${this.mruConfigService.config.substrateApiEndpoint}${path}${allQueryParams}`;

        //TODO: Skip new requests if sufficient data of desired types is present in cache
        if (this.trendingDataStreamMap.has(trendingUrl)) {
            return this.trendingDataStreamMap.get(trendingUrl);
        }

        const trendingDataFromCache = this.fetchTrendingDocData(trendingUrl, FetchStrategy.CacheOnly);
        const trendingDataFromNetwork = this.fetchTrendingDocData(trendingUrl, FetchStrategy.NetworkFirst);
        const trendingDataStream = ObservableHelper.createResultsWithCacheObservable(
            trendingDataFromCache,
            trendingDataFromNetwork,
            null,
            _ => this.trendingDataStreamMap.delete(trendingUrl)
        );

        this.trendingDataStreamMap.set(trendingUrl, trendingDataStream);

        return trendingDataStream;
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>) {
        if (paramNameInLowerCase === 'mockinsights' && this.mockRequestHeader) {
            this.requestConfig.headers.set(this.mockRequestHeader, value);
        }
    }

    private fetchTrendingDocData(serviceUrl: string, strategy: FetchStrategy): Promise<TrendingDocData> {
        return new Promise<TrendingDocData> ((resolve, reject) => {
            const promiseCallback = (promise: InstrumentedHttpPromise<TrendingResponse>) => {
                promise
                    .instrument((result, event) => {
                        event.params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        if (result.ok) {
                            if (result.data && result.data.value) {
                                const itemCount = result.data.value.length;
                                event.params[InstrumentationParam.ActualSize] = itemCount.toString();
                                event.params[InstrumentationParam.Distribution] = FileItem.distributionFromFileTypes(result.data.value.map(item => SubstrateInsightsService.fileType(item)));
                            } else {
                                event.params[InstrumentationParam.HttpCode] = HttpStatusCode.UnexpectedResponse.toString();
                            }
                        } else {
                            const errorData = (result.data as any) as TrendingErrorDataWrapper;
                            if (errorData && errorData.error && errorData.error.code) {
                                event.params[InstrumentationParam.Error] = errorData.error.code;
                            }
                        }
                    })
                    .then(result => {
                        if (result && result.data && result.data.value) {
                            resolve({
                                items: result.data.value,
                                fromCache: result.fromCache
                            });
                        } else {
                            reject(new XhrServiceError(result, 'No data returned'));
                        }
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.hLENC);
                    });
            };

             this.get<TrendingResponse>(
                 KnownInstrumentationEvents.TrendingDocumentsRequest,
                 serviceUrl,
                 promiseCallback,
                 reject,
                 this.requestConfig,
                 strategy);
        });
    }
}
